import supabase from '../config/supabase';
import {
  EQUIPMENT_CATEGORY_ORDER,
  isEquipmentCategory,
  type EquipmentCategory,
  type EquipmentItem,
} from '../constants/equipmentCategory';
import { handleServiceError } from '../utils/errorHandler';
import { insertStorageFileByPath, softDeleteStorageFileByPath } from './storageFiles';
import { requestEdgeFunction } from './edgeFunctionClient';

const EQUIPMENTS_PHOTOS_BUCKET = 'equipments-photos';
const IMAGE_FILE_KINDS = ['equipment_image', 'equipment_thumbnail'] as const;
const MAX_EQUIPMENT_IMAGES = 10;
const MAX_IMAGE_SIZE_BYTES = 60 * 1024;
const MAX_IMAGE_DIMENSION = 1600;
const MIN_IMAGE_DIMENSION = 320;

export type EquipmentCatalogRow = EquipmentItem & {
  createdAt?: string | null;
  updatedAt?: string | null;
  thumbnailUrl?: string | null;
  imageCount?: number;
};

export type EquipmentImage = {
  storagePath: string;
  url: string;
  uploadedAt: string | null;
  fileKind: string | null;
};

export type EquipmentCatalogFilters = {
  search?: string;
  categories?: EquipmentCategory[];
  isActive?: boolean[];
};

export type EquipmentCatalogPayload = {
  name: string;
  category: EquipmentCategory;
  isActive: boolean;
};

type EquipmentDbRow = {
  id: number;
  name: string;
  category: string;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

const coverUrlCache = new Map<number, string | null>();

const buildImagePath = (equipmentId: number, sequence: number): string => {
  const stamp = Date.now();
  return `${equipmentId}/${stamp}-${sequence}.jpg`;
};

const buildPublicUrl = (storagePath: string): string => {
  const { data } = supabase.storage
    .from(EQUIPMENTS_PHOTOS_BUCKET)
    .getPublicUrl(storagePath);
  return data.publicUrl;
};

const loadImageElement = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Não foi possível carregar a imagem selecionada.'));
  };

  image.src = objectUrl;
});

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Não foi possível processar a imagem selecionada.'));
      return;
    }
    resolve(blob);
  }, mimeType, quality);
});

const fitDimensions = (width: number, height: number, maxDimension: number) => {
  const largestSide = Math.max(width, height);
  if (largestSide <= maxDimension) {
    return { width, height };
  }
  const scale = maxDimension / largestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const processImageToMaxSize = async (file: File, fileName: string): Promise<File> => {
  const image = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Não foi possível preparar a compressão da imagem.');
  }

  let targetDimension = Math.min(MAX_IMAGE_DIMENSION, Math.max(image.width, image.height));

  while (targetDimension >= MIN_IMAGE_DIMENSION) {
    const { width, height } = fitDimensions(image.width, image.height, targetDimension);
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    for (const quality of [0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4]) {
      const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (blob.size <= MAX_IMAGE_SIZE_BYTES) {
        return new File([blob], fileName, { type: 'image/jpeg' });
      }
    }

    targetDimension = Math.floor(targetDimension * 0.85);
  }

  throw new Error('Não foi possível reduzir a imagem para no máximo 60 KB. Selecione uma imagem menor.');
};

const mapRow = (row: EquipmentDbRow): EquipmentCatalogRow => {
  if (!isEquipmentCategory(row.category)) {
    throw new Error(`Categoria de equipamento inválida: ${row.category}`);
  }

  return {
    id: row.id,
    name: row.name,
    category: row.category,
    isActive: row.is_active,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
};

class EquipmentCatalogServiceClass {
  get maxImages(): number {
    return MAX_EQUIPMENT_IMAGES;
  }

  async listImages(equipmentId: number): Promise<EquipmentImage[]> {
    const response = await requestEdgeFunction<{
      success?: boolean;
      data?: {
        images?: Array<{
          storage_path?: string;
          uploaded_at?: string | null;
          file_kind?: string | null;
          url?: string | null;
        }>;
      };
    }>('upload-student-photo', {
      method: 'POST',
      body: {
        action: 'list-equipment-storage-files',
        equipmentId,
      },
    }).catch((error) => {
      handleServiceError(error, 'listar imagens do equipamento');
      throw error;
    });

    return (response.data?.images ?? [])
      .filter((row) => Boolean(row.storage_path))
      .map((row) => ({
        storagePath: row.storage_path!,
        url: row.url?.trim() || buildPublicUrl(row.storage_path!),
        uploadedAt: row.uploaded_at ?? null,
        fileKind: row.file_kind ?? null,
      }));
  }

  async list(filters: EquipmentCatalogFilters = {}): Promise<EquipmentCatalogRow[]> {
    const response = await requestEdgeFunction<{
      success?: boolean;
      data?: {
        equipments?: Array<EquipmentDbRow & {
          thumbnail_url?: string | null;
          image_count?: number | null;
        }>;
      };
    }>('upload-student-photo', {
      method: 'POST',
      body: {
        action: 'list-equipment-catalog-admin',
        search: filters.search?.trim() || undefined,
        categories: filters.categories,
        isActive: filters.isActive,
      },
    }).catch((error) => {
      handleServiceError(error, 'listar equipamentos');
      throw error;
    });

    const rows = (response.data?.equipments ?? []).map((row) => ({
      ...mapRow(row),
      thumbnailUrl: row.thumbnail_url ?? null,
      imageCount: typeof row.image_count === 'number' ? row.image_count : 0,
    }));

    rows.sort((left, right) => {
      const categoryDiff = EQUIPMENT_CATEGORY_ORDER.indexOf(left.category)
        - EQUIPMENT_CATEGORY_ORDER.indexOf(right.category);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }
      return left.name.localeCompare(right.name, 'pt-BR');
    });
    return rows;
  }

  async create(
    payload: EquipmentCatalogPayload,
    imageFiles: File[] = [],
  ): Promise<EquipmentCatalogRow> {
    if (imageFiles.length > MAX_EQUIPMENT_IMAGES) {
      throw new Error(`Cada equipamento pode ter no máximo ${MAX_EQUIPMENT_IMAGES} imagens.`);
    }

    const response = await requestEdgeFunction<{ success?: boolean; data?: EquipmentDbRow }>('upload-student-photo', {
      method: 'POST',
      body: {
        action: 'create-equipment-catalog-item',
        name: payload.name.trim(),
        category: payload.category,
        isActive: payload.isActive,
      },
    }).catch((error) => {
      handleServiceError(error, 'criar equipamento');
      throw error;
    });

    const created = mapRow(response.data as EquipmentDbRow);

    if (imageFiles.length > 0) {
      await this.uploadImages(created.id, imageFiles);
    }

    const images = await this.listImages(created.id);
    return {
      ...created,
      thumbnailUrl: images[0]?.url ?? null,
      imageCount: images.length,
    };
  }

  async update(
    equipmentId: number,
    payload: EquipmentCatalogPayload,
  ): Promise<EquipmentCatalogRow> {
    const response = await requestEdgeFunction<{ success?: boolean; data?: EquipmentDbRow }>('upload-student-photo', {
      method: 'POST',
      body: {
        action: 'update-equipment-catalog-item',
        equipmentId,
        name: payload.name.trim(),
        category: payload.category,
        isActive: payload.isActive,
      },
    }).catch((error) => {
      handleServiceError(error, 'atualizar equipamento');
      throw error;
    });

    const updated = mapRow(response.data as EquipmentDbRow);
    const images = await this.listImages(equipmentId);
    coverUrlCache.delete(equipmentId);

    return {
      ...updated,
      thumbnailUrl: images[0]?.url ?? null,
      imageCount: images.length,
    };
  }

  async setActive(equipmentId: number, isActive: boolean): Promise<void> {
    await requestEdgeFunction('upload-student-photo', {
      method: 'POST',
      body: {
        action: 'set-equipment-catalog-item-active',
        equipmentId,
        isActive,
      },
    }).catch((error) => {
      handleServiceError(error, isActive ? 'ativar equipamento' : 'desativar equipamento');
      throw error;
    });

    coverUrlCache.delete(equipmentId);
  }

  async uploadImages(equipmentId: number, files: File[]): Promise<EquipmentImage[]> {
    if (files.length === 0) {
      return this.listImages(equipmentId);
    }

    const existing = await this.listImages(equipmentId);
    if (existing.length + files.length > MAX_EQUIPMENT_IMAGES) {
      throw new Error(
        `Cada equipamento pode ter no máximo ${MAX_EQUIPMENT_IMAGES} imagens `
        + `(já existem ${existing.length}).`,
      );
    }

    const { data: authData } = await supabase.auth.getUser();

    for (let index = 0; index < files.length; index += 1) {
      const storagePath = buildImagePath(equipmentId, index + 1);
      const fileName = storagePath.split('/').pop() ?? `image-${index + 1}.jpg`;
      const processedFile = await processImageToMaxSize(files[index], fileName);

      const { error } = await supabase.storage
        .from(EQUIPMENTS_PHOTOS_BUCKET)
        .upload(storagePath, processedFile, {
          upsert: false,
          contentType: processedFile.type || 'image/jpeg',
        });

      if (error) {
        handleServiceError(error, 'enviar imagem do equipamento');
      }

      await insertStorageFileByPath({
        bucketId: EQUIPMENTS_PHOTOS_BUCKET,
        storagePath,
        contentType: processedFile.type || 'image/jpeg',
        sizeBytes: processedFile.size,
        ownerType: 'equipment',
        ownerId: equipmentId,
        fileKind: 'equipment_image',
        uploadedByUserId: authData.user?.id ?? null,
      });
    }

    coverUrlCache.delete(equipmentId);
    return this.listImages(equipmentId);
  }

  async deleteImage(equipmentId: number, storagePath: string): Promise<EquipmentImage[]> {
    const normalizedPath = storagePath.replace(/^\/+/, '');
    if (!normalizedPath.startsWith(`${equipmentId}/`)) {
      throw new Error('Imagem inválida para este equipamento.');
    }

    const { error } = await supabase.storage
      .from(EQUIPMENTS_PHOTOS_BUCKET)
      .remove([normalizedPath]);

    if (error) {
      handleServiceError(error, 'remover imagem do equipamento');
    }

    await softDeleteStorageFileByPath({
      bucketId: EQUIPMENTS_PHOTOS_BUCKET,
      storagePath: normalizedPath,
    });

    coverUrlCache.delete(equipmentId);
    return this.listImages(equipmentId);
  }
}

export const EquipmentCatalogService = new EquipmentCatalogServiceClass();
export { EQUIPMENTS_PHOTOS_BUCKET, MAX_EQUIPMENT_IMAGES };
